import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { APIError } from "@/lib/types";
import {
  listPartnerFirms,
  listPartnerRequests,
  createPartnerRequest,
  removePartnerFirm,
  respondToPartnerRequest,
  type PartnerRequestDirection,
} from "@/lib/data/requests/firms/referral-partners";
import { queryKeys } from "@/lib/data/static/query-keys";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import toast from "react-hot-toast";

export const partnerFirmsQueryOptions = () => ({
  queryKey: [queryKeys.firm, "referral-partners"],
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
    listPartnerFirms(pageParam),
  getNextPageParam: (page: Awaited<ReturnType<typeof listPartnerFirms>>) =>
    page.pagination?.nextCursor,
});
export const partnerRequestsQueryOptions = (
  direction: PartnerRequestDirection,
) => ({
  queryKey: [queryKeys.firm, "referral-requests", direction],
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
    listPartnerRequests(direction, pageParam),
  getNextPageParam: (page: Awaited<ReturnType<typeof listPartnerRequests>>) =>
    page.pagination?.nextCursor,
});
function useInvalidatePartnerships() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({
        queryKey: [queryKeys.firm, "referral-partners"],
      }),
      client.invalidateQueries({
        queryKey: [queryKeys.firm, "referral-requests"],
      }),
      client.invalidateQueries({
        queryKey: [queryKeys.firm, "referral-directory"],
      }),
    ]);
}
export function useCreatePartnerRequest() {
  const invalidate = useInvalidatePartnerships();
  return useMutation({
    mutationFn: createPartnerRequest,
    onSuccess: () => {
      toast.success("Partner request sent.");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Could not send partner request.");
    },
    onSettled: invalidate,
  });
}
export function useRespondToPartnerRequest() {
  const invalidate = useInvalidatePartnerships();
  return useMutation({
    mutationFn: respondToPartnerRequest,
    onSuccess: (_data, input) => {
      toast.success(
        input.action === "accept"
          ? "Partner request accepted."
          : input.action === "decline"
            ? "Partner request declined."
            : "Partner request canceled.",
      );
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Could not update partner request.");
    },
    onSettled: invalidate,
  });
}

export function useRemovePartnerFirm() {
  const invalidate = useInvalidatePartnerships();
  return useMutation({
    mutationFn: removePartnerFirm,
    onSuccess: () => {
      toast.success("Partner removed.");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Could not remove partner.");
    },
    onSettled: invalidate,
  });
}
